import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { useHelpQuery, useDeleteHelpMutation } from '../../hooks/useHelpQuery';
import useCleanPaginationURL from '../../hooks/useCleanPaginationURL';
import { usePaginationTextReplace } from '../../hooks/usePaginationTextReplace';
import { HelpResponse, DeleteHelpsRequest } from '../../types/api/help';
import { helpTableConfig } from './HelpList+Table';
import { Placeholder, createErrorPlaceholder } from '../basic';
import RegularPageLayout from '../../layouts/RegularPageLayout';
import { useAlert } from '../../context/alert';
import TableActionsBanner from '../common/TableActionsBanner';
import { locale } from '../../helpers/strings';
import { HelpModal } from './components/HelpModal';
import { LanguageResponse } from '../../types/api/lang';
import { Translation } from '../../types/api/common';
import { fetchLanguages } from '../../services/lang';
import { pageSize } from '../../services/settings';
import { renderTruncatedText } from '../../helpers/strings';
import { Stack, Checkbox, Button } from '@primer/react'
import { Table, DataTable } from '@primer/react/experimental'
import { PlusIcon, XCircleIcon, PencilIcon, TrashIcon } from '@primer/octicons-react'

const EMPTY_SET = new Set<string>();

export default function HelpList() {
    type HelpAction = 'delete';

    const [searchParams, setSearchParams] = useSearchParams();
    const rawtabsType = searchParams.get('type');
    const tabsType = useMemo(() => rawtabsType || undefined, [rawtabsType]);

    const [pageIndex, setPageIndex] = React.useState(1)
    const [allLangs, setAllLangs] = useState<LanguageResponse[]>([]);
    const [languagesLoaded, setLanguagesLoaded] = useState(false);

    const [selectedRows, setSelectedRows] = useState<Set<string>>(EMPTY_SET);
    const [actionsAlertId, setActionsAlertId] = useState<string | undefined>(undefined);

    const [selectedFaq, setSelectedFaq] = useState<HelpResponse | undefined>(undefined);
    const [openHelpModal, setOpenHelpModal] = React.useState(false)

    const queryClient = useQueryClient();

    const { setInfo, addCustomAlert, updateAlert, removeAlert } = useAlert();

    const { mutate: deleteFaqRequest, isPending: isDeletingProcess } = useDeleteHelpMutation();

    const { mutate: fetchLanguagesRequest, isPending: isLoadingLang } = useMutation({
        mutationFn: fetchLanguages,
        onSuccess: (data) => {
            setAllLangs(data.list);
            setLanguagesLoaded(true);
        },
        onError: (error: any) => {
            setAllLangs([]);
            setLanguagesLoaded(true);
        }
    });
    const {
        isLoading: isLoadingFaq,
        data,
        isError,
        refetch,
        isFetched
    } = useHelpQuery(
        pageIndex,
        pageSize,
        undefined, //ordering
        "", //search
        undefined, //topic_id
        {
            enabled: languagesLoaded
        }
    );

    useEffect(() => {
        fetchLanguagesRequest({});
    }, []);

    const isLoading = isLoadingLang || isLoadingFaq;

    useCleanPaginationURL();
    usePaginationTextReplace()



    useEffect(() => {
        setSelectedRows(EMPTY_SET);
        refetch()
    }, [pageIndex]);

    const emptyListDescription = `В сервисе не создано
    ни одного вопроса`;

    // ACTIONS
    const useHelpAction = (action: HelpAction, actionsAlertId?: string) => {
        return useCallback(() => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const ids = Array.from(selectedItems);

                switch (action) {
                    case 'delete':
                        handleDelete([{ uuids: ids }]);
                        break;
                    default:
                        console.error('Unknown action:', action);
                }
            }
        }, [action]);
    };

    const deleteSelectedFaqs = useHelpAction('delete', actionsAlertId);

    const handleActionResult = (
        count: number,
        singularMessage: string,
        pluralMessage: string
    ) => {
        // Update UI
        dismissTableActionAlert()
        setSelectedRows(EMPTY_SET);
        refetch();
        // Alert
        if (count === 1) {
            setInfo(singularMessage);
        } else {
            setInfo(pluralMessage);
        }
    };

    // Удалить FAQ
    const handleDelete = (requests: DeleteHelpsRequest[]) => {
        if (requests.length === 0) return;

        let successCount = 0;

        requests.forEach(request => {
            deleteFaqRequest(request, {
                onSuccess: (data) => {
                    successCount++;
                    if (successCount === requests.length) {
                        dismissTableActionAlert()
                        handleActionResult(successCount, 'Вопрос удален', successCount + ' вопросы удалены');
                        refetch();
                    }
                }
            });
        });
    };

    // ALERT HELPERS
    const dismissTableActionAlert = () => {
        let alertId = actionsAlertIdRef.current
        if (alertId) {
            removeAlert(alertId)
            setActionsAlertId(undefined)
            setSelectedRows(EMPTY_SET);
        }
    }

    const actionsAlertIdRef = useRef(actionsAlertId);
    useEffect(() => {
        actionsAlertIdRef.current = actionsAlertId;
    }, [actionsAlertId])

    const selectedRowsRef = useRef(selectedRows);
    useEffect(() => {
        selectedRowsRef.current = selectedRows;
    }, [selectedRows])

    const showTableActionsAlert = useCallback(() => {
        let selectedItems = selectedRowsRef.current
        let count = Array.from(selectedItems).length
        handleAddTableAlert(count)
    }, [actionsAlertId]);

    const handleAddTableAlert = useCallback((count: number) => {
        let props: any = undefined
        props = createAlertPropsForScheduled(count)

        let alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: props
            });
        } else {
            const alertId = addCustomAlert(
                TableActionsBanner,
                props,
                {
                    autoClose: false,
                }
            );
            setActionsAlertId(alertId)
        }
    }, [actionsAlertId, data]);

    const showUpdatedAlert = useCallback((customProps: Record<string, any>) => {
        let selectedItemsCount = selectedRowsRef.current.size
        const alertId = actionsAlertIdRef.current
        if (alertId) {
            updateAlert(alertId, {
                customProps: customProps
            });
        }
    }, [actionsAlertId]);

    // TABLE ACTIONS ALERT
    function createAlertPropsForScheduled(count: number) {
        const localStr = locale.selected(count)
        const title = localStr && localStr[0].toUpperCase() + localStr.slice(1);


        const editFirstSelectedFaq = () => {
            let selectedItems = selectedRowsRef.current;

            if (selectedItems && selectedItems.size > 0) {
                const id = Array.from(selectedItems)[0];
                const listFaq = data?.list.filter(el => el.id === id) ?? []
                if (listFaq.length === 1) {
                    editSingleFaq(listFaq[0])
                    dismissTableActionAlert()
                }
            }
        }

        const buttons = [
            <Button key="clear-button"
                variant="default"
                disabled={isDeletingProcess}
                leadingVisual={PencilIcon}
                onClick={editFirstSelectedFaq}>
                Редактировать
            </Button>,
            <Button key="clear-button"
                variant="danger"
                disabled={isDeletingProcess}
                leadingVisual={TrashIcon}
                onClick={showConfirmDeleteAlert}>
                Удалить
            </Button>
        ]

        if (count > 1) {
            buttons.shift();
        }

        return {
            count: count,
            title: title,
            buttons: buttons,
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    function createConfirmDeleteAlertProps(count: number) {
        return {
            count: count,
            title: 'Точно хотите удалить?',
            buttons: [
                <Button key="clear-button" variant="default" onClick={showTableActionsAlert}>
                    Нет
                </Button>,
                <Button key="clear-button"
                    variant="danger"
                    disabled={isDeletingProcess}
                    onClick={deleteSelectedFaqs}>
                    Да, удалить
                </Button>
            ],
            onDismiss: () => { },
            didDismiss: () => {
                setActionsAlertId(undefined)
                // Uncheck table
                setSelectedRows(EMPTY_SET);
            }
        }
    }

    const showConfirmDeleteAlert = useCallback(() => {
        let selectedItemsCount = selectedRowsRef.current.size
        const props = createConfirmDeleteAlertProps(selectedItemsCount)
        showUpdatedAlert(props)
    }, [actionsAlertId]);

    // SELECT ROWS
    const handleSelectAll = useCallback((isSelected: boolean) => {
        if (data) {
            if (isSelected) {
                setSelectedRows(new Set(data.list.map(row => row.id)));
            } else {
                setSelectedRows(EMPTY_SET);
            }
        }
    }, [data]);

    const headerCheckboxState = useMemo(() => {
        if (data && data.list.length > 0) {
            const totalRows = data.list.length;
            const selectedFromCurrentData = data.list.filter(row => selectedRows.has(row.id)).length;

            if (selectedFromCurrentData === 0) {
                return { checked: false, indeterminate: false };
            } else if (selectedFromCurrentData === totalRows && totalRows > 0) {
                return { checked: true, indeterminate: false };
            } else {
                return { checked: false, indeterminate: true };
            }
        }
        return { checked: false, indeterminate: false };
    }, [data, selectedRows]);

    const handleRowSelect = useCallback((rowId: string, isSelected: boolean) => {
        if (selectedRowsRef.current.size === 0) {
            rowSelect(rowId, isSelected)
        } else {
            rowSelect(rowId, isSelected)
        }
    }, [handleAddTableAlert]);

    const rowSelect = useCallback((rowId: string, isSelected: boolean) => {
        setSelectedRows(prev => {
            const newSelection = new Set(prev);
            if (isSelected) {
                newSelection.add(rowId);
            } else {
                newSelection.delete(rowId);
            }
            return newSelection;
        });

    }, [handleAddTableAlert]);

    useEffect(() => {
        let selectedItemsCount = selectedRows.size
        if (selectedItemsCount > 0) {
            handleAddTableAlert(selectedItemsCount)
        } else {
            if (actionsAlertId) {
                removeAlert(actionsAlertId)
                setActionsAlertId(undefined)
            }
        }
    }, [selectedRows])

    const createNewFaq = () => {
        setOpenHelpModal(true)
    }

    const helpModalDismissed = () => {
        setSelectedFaq(undefined)
        setOpenHelpModal(false)
    }

    const faqSaved = () => {
        setSelectedFaq(undefined)
        setOpenHelpModal(false)
        refetch()
    }

    const editSingleFaq = (faq: HelpResponse) => {
        setSelectedFaq(faq)
        setOpenHelpModal(true)
    }

    const getRussianOrFirstFieldValue = (faq: HelpResponse, fieldName: string): string => {
        const translations = faq.translations
        const russianTranslation = translations.find(
            (t) => t.languageIsoCode === "ru"
        );
        const russianField = russianTranslation?.fields.find(
            (f) => f.fieldName === fieldName
        );

        if (russianField?.value?.trim()) {
            return russianField.value;
        }

        const firstTranslation = translations[0];
        const firstField = firstTranslation?.fields.find(
            (f) => f.fieldName === fieldName
        );

        return firstField?.value || "";
    };

    const getLangsByIsoCodes = (translations: Translation[]) => {
        return translations.map(translation => {
            const lang = allLangs.find(lang => lang.isoCode === translation.languageIsoCode);
            return lang?.titleRu || lang?.titleOrigin;
        }).filter(Boolean);
    };

    return (
        <RegularPageLayout
            header={
                <Stack justify="space-between" direction="horizontal">
                    <div></div>
                    <Button
                        variant="default"
                        size="small"
                        onClick={createNewFaq}
                        leadingVisual={PlusIcon}
                    >
                        Добавить вопрос
                    </Button>
                </Stack>
            }
            content={
                <Stack>
                    {openHelpModal && (<HelpModal
                        key={selectedFaq?.id || 'key'}
                        isOpen={openHelpModal}
                        faq={selectedFaq}
                        onDidSaved={helpModalDismissed}
                        onClose={faqSaved}
                    />)}

                    {isError && !isLoading && createErrorPlaceholder()}
                    {isLoading && <Table.Container>
                        <Table.Skeleton
                            aria-labelledby="help-loading"
                            rows={pageSize}
                            columns={helpTableConfig().map((el) => {
                                return {
                                    header: el.header,
                                    id: el.field,
                                }
                            })}
                        />
                    </Table.Container>}
                    {isFetched && data?.list.length === 0 && (
                        <Placeholder
                            icon={<XCircleIcon size={24} className='opacity-40' />}
                            header='Вопросы не найдены'
                            description={emptyListDescription}
                            button={undefined}
                        />
                    )}
                    {isFetched && data && data.list.length > 0 &&
                        <Table.Container>
                            <DataTable
                                aria-labelledby="help-table"
                                data={data.list}
                                columns={[
                                    ...(tabsType === 'all' || tabsType === undefined || tabsType === 'scheduled' ? [{
                                        id: 'select',
                                        header: (() => <Checkbox
                                            checked={headerCheckboxState.checked}
                                            indeterminate={headerCheckboxState.indeterminate}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                handleSelectAll(e.target.checked)
                                            }
                                            aria-label="Выделить все строки"
                                        />),
                                        field: undefined,
                                        rowHeader: true,
                                        maxWidth: 40,
                                        renderCell: (faq: HelpResponse) => {
                                            return (
                                                <Checkbox
                                                    checked={selectedRows.has(faq.id)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleRowSelect(faq.id, e.target.checked)
                                                    }
                                                    aria-label={`Выделить ${faq.id}`}
                                                />
                                            )
                                        }
                                    }] : []),

                                    {
                                        id: 'question',
                                        header: 'Вопрос',
                                        renderCell: (row) => {
                                            const faq = row as HelpResponse;
                                            return renderTruncatedText(getRussianOrFirstFieldValue(faq, 'title'), 64)
                                        }
                                    },
                                    {
                                        id: 'answer',
                                        header: 'Ответ',
                                        renderCell: (row) => {
                                            const faq = row as HelpResponse;
                                            return renderTruncatedText(getRussianOrFirstFieldValue(faq, 'body'), 64)
                                        }
                                    },
                                    {
                                        id: 'langs',
                                        header: 'Языки',
                                        renderCell: (row) => {
                                            const faq = row as HelpResponse;
                                            const titleLangs = getLangsByIsoCodes(faq.translations)
                                            const langs = titleLangs
                                                .map((title, index) => index === 0 ? title : title?.toLowerCase())
                                                .join(', ');
                                            return renderTruncatedText(langs, 32)
                                        }
                                    },
                                    {
                                        id: 'actions',
                                        header: '',
                                        renderCell: (row) => {
                                            const faq = row as HelpResponse;
                                            return (
                                                <Button
                                                    onClick={() => editSingleFaq(faq)}
                                                    disabled={selectedRows.size !== 0 || isDeletingProcess}
                                                    size="small"
                                                    className={selectedRows.size !== 0 ? 'opacity-50' : ''}>
                                                    Редактировать</Button>
                                            )
                                        }
                                    }
                                ]}
                            />
                        </Table.Container>
                    }
                    {isFetched && data && data.list.length > 0 &&
                        (<div className="sticky bottom-0 bg-white border-t border-gray-200 z-10 -mt-4">
                            <Table.Pagination aria-label="pagination" pageSize={pageSize} totalCount={data.totalCount} onChange={({
                                pageIndex
                            }) => {
                                setPageIndex(pageIndex + 1);
                            }} defaultPageIndex={pageIndex - 1} />
                        </div>
                        )}
                </Stack>
            }
        ></RegularPageLayout>

    )
}
